// Sprint 17 A8: CRM Deals page — pipeline kanban with AI indicators
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useDealList, usePipelines, createDeal, createPipeline } from "@/hooks/use-deals";
import type { CreateDealInput, CreatePipelineInput } from "@/hooks/use-deals";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import { CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE } from "@ease/crm/types";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton, SkeletonKanbanCard } from "@/components/skeleton";
import { KanbanBoard } from "./kanban-board";
import { DealDetail } from "./deal-detail";
import { PipelineAIBar } from "@/components/crm/pipeline-ai-bar";
import { CreateDealModal, type CreateDealData } from "@/components/crm/create-deal-modal";
import { PipelineConfigPanel } from "./pipeline-config-panel";
import { useTerminology } from "@/lib/terminology";
import { CheckCircle2, Plus, Settings } from "lucide-react";

const PIPELINE_TEMPLATES: CreatePipelineInput[] = [
  {
    name: "Sales Pipeline",
    isDefault: true,
    stages: [
      { id: "new", name: "New", color: "#3B82F6" },
      { id: "qualified", name: "Qualified", color: "#8B5CF6" },
      { id: "proposal", name: "Proposal", color: "#F59E0B" },
      { id: "won", name: "Won", color: "#10B981", isWinStage: true },
      { id: "lost", name: "Lost", color: "#6B7280", isLossStage: true },
    ],
  },
  {
    name: "Appointments",
    isDefault: true,
    stages: [
      { id: "inquiry", name: "Inquiry", color: "#0EA5E9" },
      { id: "consultation", name: "Consultation", color: "#6366F1" },
      { id: "scheduled", name: "Scheduled", color: "#F59E0B" },
      { id: "completed", name: "Completed", color: "#10B981", isWinStage: true },
      { id: "lost", name: "Lost", color: "#6B7280", isLossStage: true },
    ],
  },
  {
    name: "Membership Journey",
    isDefault: true,
    stages: [
      { id: "lead", name: "Lead", color: "#3B82F6" },
      { id: "trial", name: "Trial", color: "#8B5CF6" },
      { id: "offer", name: "Offer", color: "#F59E0B" },
      { id: "active", name: "Active", color: "#10B981", isWinStage: true },
      { id: "churned", name: "Churned", color: "#6B7280", isLossStage: true },
    ],
  },
  {
    name: "Case Pipeline",
    isDefault: true,
    stages: [
      { id: "consultation", name: "Consultation", color: "#3B82F6" },
      { id: "retained", name: "Retained", color: "#8B5CF6" },
      { id: "in_progress", name: "In Progress", color: "#F59E0B" },
      { id: "resolved", name: "Resolved", color: "#10B981", isWinStage: true },
      { id: "closed", name: "Closed", color: "#6B7280", isLossStage: true },
    ],
  },
  {
    name: "Project Pipeline",
    isDefault: true,
    stages: [
      { id: "brief", name: "Brief", color: "#06B6D4" },
      { id: "scope", name: "Scope", color: "#6366F1" },
      { id: "delivery", name: "Delivery", color: "#F59E0B" },
      { id: "complete", name: "Complete", color: "#10B981", isWinStage: true },
      { id: "cancelled", name: "Cancelled", color: "#6B7280", isLossStage: true },
    ],
  },
  {
    name: "Custom Starter",
    isDefault: true,
    stages: [
      { id: "stage_1", name: "Stage 1", color: "#3B82F6" },
      { id: "stage_2", name: "Stage 2", color: "#8B5CF6" },
      { id: "stage_3", name: "Stage 3", color: "#F59E0B" },
      { id: "won", name: "Won", color: "#10B981", isWinStage: true },
      { id: "lost", name: "Lost", color: "#6B7280", isLossStage: true },
    ],
  },
];

/** CRM Deals page — pipeline kanban + AI indicators + deal detail slide-over. */
export default function DealsPage() {
  const navigate = useNavigate();
  const { dealId: routeDealId } = useParams<{ dealId?: string }>();
  const { pipelines, isLoading: pipelinesLoading, refetch: refetchPipelines } = usePipelines();
  const { data: organizationMembers } = useOrganizationMembers();
  const terminology = useTerminology();
  const [activePipelineIdx, setActivePipelineIdx] = useState(0);
  const [ownerFilterId, setOwnerFilterId] = useState("");
  const ownerFilterOptions = organizationMembers ?? [];
  const ownerLabelById = useMemo(
    () => new Map(ownerFilterOptions.map((member) => [member.id, formatOrganizationMemberLabel(member)])),
    [ownerFilterOptions],
  );

  const activePipeline = pipelines[activePipelineIdx];
  const { deals, isLoading: dealsLoading, error, refetch } = useDealList(
    activePipeline?.id,
    ownerFilterId || undefined,
  );

  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPipelineConfig, setShowPipelineConfig] = useState(false);
  const [showPipelineTemplates, setShowPipelineTemplates] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isCreatingPipeline, setIsCreatingPipeline] = useState<string | null>(null);

  const activeDealId = routeDealId ?? selectedDealId;

  const handleSelectDeal = (dealId: string | null) => {
    setSelectedDealId(dealId);
    navigate(dealId ? `/crm/deals/${dealId}` : "/crm/deals");
  };

  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreateDeal = async (data: CreateDealData) => {
    setIsCreating(true);
    setCreateError(null);
    try {
      await createDeal(data as CreateDealInput);
      setShowCreateModal(false);
      refetch();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create deal");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreatePipeline = async (template: CreatePipelineInput) => {
    setIsCreatingPipeline(template.name);
    setCreateError(null);
    try {
      const created = await createPipeline({
        ...template,
        isDefault: pipelines.length === 0,
      });
      await refetchPipelines();
      if (created.id) {
        setActivePipelineIdx(Math.max(0, pipelines.length));
      }
      setShowPipelineTemplates(false);
      navigate("/crm/deals", { replace: true });
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : `Failed to create ${terminology.pipeline.toLowerCase()}`);
    } finally {
      setIsCreatingPipeline(null);
    }
  };

  if (pipelinesLoading || dealsLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="flex gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-72 space-y-3 rounded-lg border border-border bg-muted/30 p-3">
              <Skeleton className="h-6 w-32" />
              <SkeletonKanbanCard />
              <SkeletonKanbanCard />
              <SkeletonKanbanCard />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (pipelines.length === 0) {
    return (
      <section className="space-y-6 rounded-3xl border border-[var(--color-border)] bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.10),_transparent_48%),var(--color-background)] p-6">
        <div className="max-w-2xl space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-primary)]">
            {terminology.pipeline} setup
          </p>
          <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">
            Create your first {terminology.pipeline.toLowerCase()} without leaving the board
          </h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Choose a real starter template, mount it immediately, and come back later for deeper {terminology.pipeline.toLowerCase()} management.
          </p>
        </div>

        {createError ? (
          <div className="rounded-xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
            {createError}
          </div>
        ) : null}

        <PipelineTemplatePicker
          templates={PIPELINE_TEMPLATES}
          isCreatingPipeline={isCreatingPipeline}
          onCreate={handleCreatePipeline}
          dealLabel={terminology.deals}
        />
      </section>
    );
  }

  return (
    <div className="density-gap space-y-4" data-reveal>
      {/* Header with pipeline selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-[var(--color-foreground)]">{terminology.deals}</h1>
          <Link
            to="/crm/forecast"
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            Forecast
          </Link>
          <Link
            to="/crm/risk-radar"
            className="rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            Risk Radar
          </Link>
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="btn-press flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors"
            style={{ background: "var(--gradient-energy)" }}
          >
            <Plus className="h-4 w-4" />
            Add {terminology.deal}
          </button>
          {activePipeline && (
            <button
              type="button"
              onClick={() => setShowPipelineConfig((current) => !current)}
              className="btn-press flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
            >
              <Settings className="h-4 w-4" />
              Configure {terminology.pipeline.toLowerCase()}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowPipelineTemplates((current) => !current)}
            className="btn-press flex items-center gap-1.5 rounded-lg border border-[var(--color-border)] px-4 py-2 text-sm font-medium text-[var(--color-foreground)] transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
          >
            <Plus className="h-4 w-4" />
            New {terminology.pipeline.toLowerCase()}
          </button>
          {pipelines.length > 1 && (
            <select
              value={activePipelineIdx}
              onChange={(e) => setActivePipelineIdx(Number(e.target.value))}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)]"
            >
              {pipelines.map((p, i) => (
                <option key={p.id} value={i}>{p.name}</option>
              ))}
            </select>
          )}
          <label className="flex items-center gap-2 text-xs font-medium text-[var(--color-muted-foreground)]">
            Owner
            <select
              aria-label="Filter deals by owner"
              value={ownerFilterId}
              onChange={(event) => setOwnerFilterId(event.target.value)}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-1.5 text-sm text-[var(--color-foreground)]"
            >
              <option value="">All owners</option>
              <option value={CRM_UNASSIGNED_ASSIGNEE_FILTER_VALUE}>Unassigned</option>
              {ownerFilterOptions.map((member) => (
                <option key={member.id} value={member.id}>
                  {formatOrganizationMemberLabel(member)}
                </option>
              ))}
            </select>
          </label>
          {ownerFilterId ? (
            <button
              type="button"
              onClick={() => setOwnerFilterId("")}
              className="rounded-md border border-[var(--color-border)] px-2.5 py-1.5 text-xs font-medium text-[var(--color-muted-foreground)] transition-colors hover:text-[var(--color-foreground)]"
            >
              Clear owner
            </button>
          ) : null}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]">
          {error}
          <button onClick={refetch} className="ml-2 underline">Retry</button>
        </div>
      )}

      {/* Pipeline AI indicators */}
      {activePipeline && <PipelineAIBar pipelineId={activePipeline.id} />}

      {activePipeline && showPipelineConfig && (
        <PipelineConfigPanel
          pipeline={activePipeline}
          onClose={() => setShowPipelineConfig(false)}
          onChanged={async () => {
            await refetchPipelines();
            refetch();
          }}
        />
      )}

      {showPipelineTemplates && (
        <section className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 shadow-sm">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--color-primary)]">
                New {terminology.pipeline.toLowerCase()}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-[var(--color-foreground)]">
                Add another live board
              </h2>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Create a second pipeline for a different motion without replacing the current default board.
              </p>
            </div>
          </div>
          <PipelineTemplatePicker
            templates={PIPELINE_TEMPLATES}
            isCreatingPipeline={isCreatingPipeline}
            onCreate={handleCreatePipeline}
            dealLabel={terminology.deals}
          />
        </section>
      )}

      {/* Kanban or empty */}
      {activePipeline && (
        deals.length === 0 && !error ? (
          <EmptyState
        icon="pipeline"
        title={`No ${terminology.deals.toLowerCase()} yet`}
        description={`Create your first ${terminology.deal.toLowerCase()} to start tracking live revenue opportunities.`}
        actionLabel={`Add ${terminology.deal.toLowerCase()}`}
        onAction={() => setShowCreateModal(true)}
      />
        ) : (
          <KanbanBoard
            stages={activePipeline.stages}
            deals={deals}
            pipelineId={activePipeline.id}
            ownerLabelById={ownerLabelById}
            onDealClick={handleSelectDeal}
            onRefresh={refetch}
          />
        )
      )}

      {/* Deal detail */}
      {activeDealId && (
        <DealDetail
          dealId={activeDealId}
          onClose={() => handleSelectDeal(null)}
        />
      )}

      {/* Create deal modal */}
      {showCreateModal && activePipeline && (
        <CreateDealModal
          pipelineId={activePipeline.id}
          stages={activePipeline.stages}
          onSubmit={handleCreateDeal}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={isCreating}
        />
      )}
    </div>
  );
}

function formatOrganizationMemberLabel(member: { name: string | null; email: string }): string {
  return member.name?.trim() || member.email;
}

function PipelineTemplatePicker({
  templates,
  isCreatingPipeline,
  onCreate,
  dealLabel,
}: {
  templates: CreatePipelineInput[];
  isCreatingPipeline: string | null;
  onCreate: (template: CreatePipelineInput) => void;
  dealLabel: string;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {templates.map((template) => (
        <button
          key={template.name}
          type="button"
          onClick={() => onCreate(template)}
          disabled={isCreatingPipeline !== null}
          className="group rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[var(--color-primary)] hover:shadow-[0_16px_40px_rgba(15,23,42,0.08)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold text-[var(--color-foreground)]">{template.name}</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                {template.stages.length} stages ready for live {dealLabel.toLowerCase()}.
              </p>
            </div>
            {isCreatingPipeline === template.name ? (
              <span className="text-xs font-medium text-[var(--color-primary)]">Creating...</span>
            ) : (
              <CheckCircle2 className="h-5 w-5 text-[var(--color-primary)]/60 transition-colors group-hover:text-[var(--color-primary)]" />
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {template.stages.map((stage) => (
              <span
                key={stage.id}
                className="rounded-full px-2.5 py-1 text-xs font-medium text-white"
                style={{ backgroundColor: stage.color }}
              >
                {stage.name}
              </span>
            ))}
          </div>
        </button>
      ))}
    </div>
  );
}
